import React from 'react'
import styled from 'styled-components'
import { Form, Col, InputGroup, Row } from 'react-bootstrap'
import Select from 'react-select'
import Checkbox from '../../../../components/component-items/checkbox'
import { HoverNotes } from '../../../../components/component-items/hover-notes'
import { WarehouseSetupFieldsProps } from '../types'
import { WarehouseManagementSystem } from '../../../../enums'
import { globalState } from '../../../../store'

const MultiSelect = styled.div`
  .basic-multi-select > div {
    min-height: 40px;
  }
`

export const WarehouseManagementFields = ({
  settings,
  setSettings,
  warehouseList,
  warehouses,
  context,
}: WarehouseSetupFieldsProps) => {
  const {
    state: { backorderManagementEnabled },
  } = globalState()

  const showField = (field: string) => {
    if (context === 'warehouse') {
      if (field === 'autoWaveOrders') {
        return settings.wms !== WarehouseManagementSystem.Local
      }
      if (field === 'orderRoutingWarehouses' || field === 'useOnlyInStockWarehouses') {
        return !backorderManagementEnabled
      }
      return true
    }
    // Merchant context: show minimumRemainingShelfLife always,
    // and useOnlyInStockWarehouses only when backorder management is off.
    if (field === 'minimumRemainingShelfLife') return true
    if (field === 'useOnlyInStockWarehouses') return !backorderManagementEnabled
    return false
  }

  return (
    <>
      <Row style={{ marginTop: '.8em', marginBottom: '2em' }}>
        {showField('warehouse') && (
          <Form.Group as={Col} md="6" controlId="warehouse" className="required">
            <Form.Label>Default Warehouse</Form.Label>
            <Form.Select
              value={settings.warehouse}
              onChange={(e) => setSettings({ ...settings, warehouse: e.target.value })}
            >
              {warehouseList.map((w: any) => (
                <option value={w.id} key={w.id}>
                  {w.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        {showField('minimumRemainingShelfLife') && (
          <Form.Group as={Col} md="6" controlId="minimumRemainingShelfLife">
            <Form.Label>
              <HoverNotes
                description="Specifies the minimum shelf life for lot controlled inventory in days."
                title="Minimum Shelf Life"
              />
            </Form.Label>
            <Form.Control
              type="number"
              required
              value={settings.minimumRemainingShelfLife}
              onChange={(e) => setSettings({ ...settings, minimumRemainingShelfLife: e.target.value })}
            />
          </Form.Group>
        )}
        {showField('activeWarehouses') && (
          <Form.Group as={Col} md="6" controlId="activeWarehouses">
            <Form.Label>
              <HoverNotes description="Only applies to multi-warehouse merchants." title="Active Warehouses" />
            </Form.Label>
            <MultiSelect style={{ marginBottom: '1em' }}>
              <Select
                options={warehouses}
                value={settings.activeWarehouses}
                onChange={(e: any) => setSettings({ ...settings, activeWarehouses: e })}
                isMulti
              />
            </MultiSelect>
          </Form.Group>
        )}
        {showField('orderRoutingWarehouses') && (
          <Form.Group as={Col} md="6" controlId="orderRoutingWarehouses">
            <Form.Label>
              <HoverNotes
                description="Imported orders will choose from this list."
                title="Warehouses Accepting Orders"
              />
            </Form.Label>
            <MultiSelect style={{ marginBottom: '1em' }}>
              <Select
                options={settings.activeWarehouses}
                value={settings.orderRoutingWarehouses}
                onChange={(e: any) => setSettings({ ...settings, orderRoutingWarehouses: e })}
                isMulti
              />
            </MultiSelect>
          </Form.Group>
        )}
        {showField('wms') && (
          <Form.Group as={Col} md="6" controlId="wms" className="required">
            <Form.Label>Warehouse Management System</Form.Label>
            <Form.Select value={settings.wms} onChange={(e) => setSettings({ ...settings, wms: e.target.value })}>
              {Object.keys(WarehouseManagementSystem).map((key) => {
                const value = WarehouseManagementSystem[key as keyof typeof WarehouseManagementSystem]
                return (
                  <option key={value} value={value}>
                    {key}
                  </option>
                )
              })}
            </Form.Select>
          </Form.Group>
        )}
        {showField('maId') && (
          <Form.Group as={Col} md="6" controlId="maId">
            <Form.Label>
              <HoverNotes
                description="This value is also used as the E1 Customer Number (Long Address Number) for billing integration with JDE E1."
                title="Manhattan Active Company ID"
              />
            </Form.Label>
            <Form.Control
              type="text"
              required={settings.wms === WarehouseManagementSystem['Manhattan Active']}
              value={settings.maId || ''}
              onChange={(e) => setSettings({ ...settings, maId: e.target.value })}
            />
          </Form.Group>
        )}
      </Row>
      <Row style={{ marginBottom: '2em' }}>
        {showField('fastPick') && (
          <Col md="12">
            <InputGroup>
              <Checkbox
                selected={settings.fastPick}
                setSelected={() => setSettings({ ...settings, fastPick: !settings.fastPick })}
              />
              <p style={{ padding: '0 1em' }}>Enable Fast Wave</p>
            </InputGroup>
          </Col>
        )}

        {showField('autoWaveOrders') && (
          <Col md="12">
            <InputGroup>
              <Checkbox
                selected={settings.autoWaveOrders}
                setSelected={() => setSettings({ ...settings, autoWaveOrders: !settings.autoWaveOrders })}
              />
              <p style={{ padding: '0 1em' }}>Enable Auto Wave Orders</p>
            </InputGroup>
          </Col>
        )}

        {showField('enableFulfillment') && (
          <Col md="12">
            <InputGroup>
              <Checkbox
                selected={settings.enableFulfillment}
                setSelected={() => setSettings({ ...settings, enableFulfillment: !settings.enableFulfillment })}
              />
              <p style={{ padding: '0 1em' }}>Enable Fulfillment</p>
            </InputGroup>
          </Col>
        )}

        {showField('useOnlyInStockWarehouses') && (
          <Col md="12">
            <InputGroup>
              <Checkbox
                selected={settings.useOnlyInStockWarehouses}
                setSelected={() =>
                  setSettings({ ...settings, useOnlyInStockWarehouses: !settings.useOnlyInStockWarehouses })
                }
              />
              <p style={{ padding: '0 1em' }}>Use Only In-Stock Warehouses</p>
            </InputGroup>
          </Col>
        )}

        {showField('active') && (
          <Col md="12">
            <InputGroup>
              <Checkbox
                selected={settings.active}
                setSelected={() => setSettings({ ...settings, active: !settings.active })}
              />
              <p style={{ padding: '0 1em' }}>Company Is Active</p>
            </InputGroup>
          </Col>
        )}

        {showField('testing') && (
          <Col md="12">
            <InputGroup>
              <Checkbox
                selected={settings.testing}
                setSelected={() => setSettings({ ...settings, testing: !settings.testing })}
              />
              <p style={{ padding: '0 1em' }}>Test Company</p>
            </InputGroup>
          </Col>
        )}
      </Row>
    </>
  )
}
